import type { WorkflowTourStep } from './types';

export const WORKFLOW_TOUR_STEP_DURATION = 6500;
/** Fractions of a step's duration: the simulated drag starts at the first and the step is applied at the second. */
export const WORKFLOW_TOUR_DRAG_START = 0.15;
export const WORKFLOW_TOUR_APPLY_AT = 0.72;

export type WorkflowTourPhase =
  // The page is reconstructing the step's starting state and scrolling its targets into view.
  | { kind: 'preparing' }
  | { kind: 'unavailable' }
  | { kind: 'running'; elapsed: number; applied: boolean }
  // The last step has played through.
  | { kind: 'finished' };

export interface WorkflowTourTimeline {
  step: number;
  playing: boolean;
  phase: WorkflowTourPhase;
}

export type WorkflowTourEvent =
  | { kind: 'goTo'; step: number }
  | { kind: 'replay' }
  | { kind: 'targetsMissing' }
  | { kind: 'positioned' }
  | { kind: 'tick'; delta: number }
  | { kind: 'togglePlay' };

/** prepare: reconstruct the state before a step and locate its targets. perform: simulate the step. */
export type WorkflowTourEffect = { kind: 'prepare'; stepID: string } | { kind: 'perform'; stepID: string };

export interface WorkflowTourContext {
  steps: readonly WorkflowTourStep[];
  reducedMotion: boolean;
}

export interface WorkflowTourTransition {
  timeline: WorkflowTourTimeline;
  effects: WorkflowTourEffect[];
}

export function workflowTourTimelineStart(context: WorkflowTourContext): WorkflowTourTransition {
  return stepEnter(context, { step: 0, playing: !context.reducedMotion, phase: { kind: 'preparing' } }, 0);
}

export function workflowTourTimelineUpdate(
  context: WorkflowTourContext,
  timeline: WorkflowTourTimeline,
  event: WorkflowTourEvent,
): WorkflowTourTransition {
  const unchanged = { timeline, effects: [] };
  const { phase } = timeline;
  switch (event.kind) {
    case 'goTo':
      if (event.step < 0 || event.step >= context.steps.length) return unchanged;
      return stepEnter(context, timeline, event.step);
    case 'replay':
      return stepEnter(context, { ...timeline, playing: !context.reducedMotion }, 0);
    case 'targetsMissing':
      if (phase.kind !== 'preparing') return unchanged;
      return { timeline: { ...timeline, playing: false, phase: { kind: 'unavailable' } }, effects: [] };
    case 'positioned': {
      if (phase.kind !== 'preparing') return unchanged;
      // Without motion there is no drag to watch, so the step is applied as soon as it is in view.
      const applied = context.reducedMotion;
      return {
        timeline: { ...timeline, phase: { kind: 'running', elapsed: 0, applied } },
        effects: applied ? [performEffect(context, timeline.step)] : [],
      };
    }
    case 'togglePlay':
      if (phase.kind !== 'running') return unchanged;
      return { timeline: { ...timeline, playing: !timeline.playing }, effects: [] };
    case 'tick': {
      if (phase.kind !== 'running' || !timeline.playing) return unchanged;
      const elapsed = phase.elapsed + event.delta;
      const progress = Math.min(elapsed / WORKFLOW_TOUR_STEP_DURATION, 1);
      const effects: WorkflowTourEffect[] = [];
      const applied = phase.applied || progress >= WORKFLOW_TOUR_APPLY_AT;
      if (applied && !phase.applied) effects.push(performEffect(context, timeline.step));
      if (progress < 1) return { timeline: { ...timeline, phase: { kind: 'running', elapsed, applied } }, effects };
      if (timeline.step === context.steps.length - 1) {
        return { timeline: { ...timeline, playing: false, phase: { kind: 'finished' } }, effects };
      }
      const next = stepEnter(context, timeline, timeline.step + 1);
      return { timeline: next.timeline, effects: [...effects, ...next.effects] };
    }
  }
}

/** How far through the current step the timeline is, from 0 to 1. */
export function workflowTourProgress(context: WorkflowTourContext, timeline: WorkflowTourTimeline): number {
  switch (timeline.phase.kind) {
    case 'preparing':
    case 'unavailable':
      return 0;
    case 'running':
      return Math.min(timeline.phase.elapsed / WORKFLOW_TOUR_STEP_DURATION, 1);
    case 'finished':
      return 1;
  }
}

function stepEnter(context: WorkflowTourContext, timeline: WorkflowTourTimeline, step: number): WorkflowTourTransition {
  return {
    timeline: { ...timeline, step, phase: { kind: 'preparing' } },
    effects: [{ kind: 'prepare', stepID: context.steps[step]!.id }],
  };
}

function performEffect(context: WorkflowTourContext, step: number): WorkflowTourEffect {
  return { kind: 'perform', stepID: context.steps[step]!.id };
}
